/**
 * Step 7 (Platform-Eng workflow) — PolicyDecisionsInventory.
 *
 * <p>Renders the new {@code /policy-config?tab=decisions} surface
 * that closes the second Tier-1 gap from the platform-engineer
 * walkthrough: until this shipped, a denied SRE call yielded a
 * generic 403 in the chat panel with no named-reason surface,
 * because no React surface rendered the {@code policy_decisions}
 * table (rows have been written since V35).</p>
 *
 * <h3>Surface</h3>
 *
 * <ul>
 *   <li>Filter bar — decision discriminator pill toggle
 *       (All / Allowed / Blocked / Escalated), three UUID inputs
 *       (agentId / skillId / userId), and a rolling time-window
 *       picker (1h / 24h / 72h / 7d / all time).</li>
 *   <li>Paginated v2 Table — most-recent-first, columns:
 *       decision (color-coded pill), evaluated-at, user email
 *       (resolved server-side via AppUserRepository), agent +
 *       skill UUIDs (truncated), rule name, violation reason.</li>
 *   <li>Row click — opens {@link PolicyDecisionDetailSlideout}
 *       with the full row including the {@code contextData} input
 *       bundle the rule actually saw.</li>
 * </ul>
 *
 * <h3>RBAC + tenancy</h3>
 *
 * <p>Server-side {@code policy.read} permission is enforced; the
 * tenant scope is pulled from the JWT at the backend, so this
 * component can't accidentally leak cross-tenant rows even if a
 * caller tampered with the URL.</p>
 *
 * <h3>Production hygiene</h3>
 *
 * <p>Backend errors surface in an inline banner, not a toast — the
 * filter bar stays interactive so the operator can adjust scope
 * without losing the page state. Empty results show an EmptyState
 * card with a "Clear filters" CTA so the operator can recover from
 * a too-narrow filter without back-navigating.</p>
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  RefreshCw, Search, AlertTriangle, ShieldCheck, ShieldX, ShieldAlert,
  Filter as FilterIcon, Clock,
} from 'lucide-react';
import {
  Button, Pill, Card, Table, EmptyState, MetricCard,
} from '../index';
import { Pagination, usePaginationState } from '../../common/Pagination';
import {
  listPolicyDecisions, getPolicyDecision,
} from '../../../services/api';
import { PolicyDecisionDetailSlideout } from './PolicyDecisionDetailSlideout';

/* Step 7 — decision discriminator → pill style. Matches the
 * Skill backend's PolicyDecision constants (ALLOWED / BLOCKED /
 * ESCALATED). 'escalated' shows in amber because semantically
 * the decision was "punt to human" rather than allow/deny. */
const DECISION_PILL = {
  allowed:   { tone: 'success', label: 'allowed',   icon: ShieldCheck },
  blocked:   { tone: 'danger',  label: 'blocked',   icon: ShieldX },
  escalated: { tone: 'warning', label: 'escalated', icon: ShieldAlert },
};

const HOURS_OPTIONS = [
  { value: 1,   label: 'Last 1h' },
  { value: 24,  label: 'Last 24h' },
  { value: 72,  label: 'Last 3d' },
  { value: 168, label: 'Last 7d' },
  { value: 0,   label: 'All time' },
];

const DECISION_FILTER_OPTIONS = [
  { value: '',          label: 'All' },
  { value: 'allowed',   label: 'Allowed' },
  { value: 'blocked',   label: 'Blocked' },
  { value: 'escalated', label: 'Escalated' },
];

function shortUuid(v) {
  if (!v) return '—';
  const s = String(v);
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
}

function formatTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return String(iso); }
}

export function PolicyDecisionsInventory() {
  // Filter state
  const [decision, setDecision] = useState('');
  const [hours, setHours]       = useState(24);
  const [agentId, setAgentId]   = useState('');
  const [skillId, setSkillId]   = useState('');
  const [userId, setUserId]     = useState('');

  // Data state
  const [rows, setRows]   = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages]       = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Pagination state
  const pg = usePaginationState(50);

  // Drill-down state
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedError, setSelectedError]     = useState(null);

  /* Backend pagination is page+size; v2 Pagination component
   * exposes page (0-indexed) + pageSize. We translate by passing
   * pg.page directly and reading the server's totalElements back
   * into pg via setTotalItems (Pagination respects the prop). */
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await listPolicyDecisions({
      decision: decision || undefined,
      agentId:  agentId.trim() || undefined,
      skillId:  skillId.trim() || undefined,
      userId:   userId.trim()  || undefined,
      hours,
      page: pg.page,
      size: pg.pageSize,
    });
    if (r?.ok === false) {
      setError(r.error || 'Failed to load decisions');
      setRows([]); setTotalElements(0); setTotalPages(0);
      setLoading(false);
      return;
    }
    setRows(Array.isArray(r?.items) ? r.items : []);
    setTotalElements(Number(r?.totalElements) || 0);
    setTotalPages(Number(r?.totalPages) || 0);
    setLoading(false);
  }, [decision, hours, agentId, skillId, userId, pg.page, pg.pageSize]);

  useEffect(() => { reload(); }, [reload]);

  /* Reset to page 0 when a filter changes — otherwise an out-of-
   * range page index sticks and the table looks empty on a fresh
   * filter combination that has fewer than (currentPage*size) rows. */
  useEffect(() => { pg.setPage(0); }, [decision, hours, agentId, skillId, userId]); // eslint-disable-line

  const clearFilters = () => {
    setDecision(''); setHours(24); setAgentId(''); setSkillId(''); setUserId('');
  };

  // Open the detail slideout — fetches the full row including contextData.
  const onRowClick = useCallback(async (row) => {
    if (!row?.id) return;
    setSelectedId(row.id);
    setSelectedDetail(null);
    setSelectedError(null);
    setSelectedLoading(true);
    const r = await getPolicyDecision(row.id);
    if (r?.ok === false) {
      setSelectedError(r.error || 'Failed to load decision detail');
      setSelectedLoading(false);
      return;
    }
    setSelectedDetail(r);
    setSelectedLoading(false);
  }, []);
  const closeDetail = () => {
    setSelectedId(null); setSelectedDetail(null); setSelectedError(null);
  };

  // ── KPI strip (current page slice). Server gives us totalElements
  // for the full filtered set; we render that as the "Total" tile so
  // the operator sees the size of the filter, not just the page. ──
  const kpis = useMemo(() => {
    let allowed = 0, blocked = 0, escalated = 0;
    for (const r of rows) {
      if (r.decision === 'allowed')    allowed++;
      else if (r.decision === 'blocked')   blocked++;
      else if (r.decision === 'escalated') escalated++;
    }
    return { totalFiltered: totalElements, allowed, blocked, escalated };
  }, [rows, totalElements]);

  const inputClass =
    'v2-focus-ring w-full px-2.5 py-1.5 rounded-v2-sm border border-v2-border ' +
    'bg-v2-surface text-v2-ink text-[12px]';

  return (
    <div>
      {/* KPIs — page-scoped counts + filter total. Same pattern the
          Policy Skills tab uses. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Filtered total"      value={kpis.totalFiltered.toLocaleString()} tone="cyan" />
        <MetricCard label="Allowed (page)"      value={kpis.allowed}     tone="success" />
        <MetricCard label="Blocked (page)"      value={kpis.blocked}     tone="danger" />
        <MetricCard label="Escalated (page)"    value={kpis.escalated}   tone="warning" />
      </div>

      {/* Filter bar */}
      <Card padding="sm">
        <Card.Header title="Filters" actions={
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>
              Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        } />
        <Card.Body>
          <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr' }}>
            <div>
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                <FilterIcon size={10} style={{ display: 'inline', marginRight: 4 }} /> Decision
              </span>
              <div className="flex items-center gap-1 flex-wrap">
                {DECISION_FILTER_OPTIONS.map((o) => (
                  <button
                    key={o.value || 'all'}
                    type="button"
                    onClick={() => setDecision(o.value)}
                    className={`inline-flex items-center px-2 py-0.5 rounded-v2-pill text-[11px] font-medium border v2-focus-ring ${
                      decision === o.value
                        ? 'bg-v2-action-soft text-v2-action-strong border-v2-action/30'
                        : 'bg-v2-surface text-v2-ink-muted border-v2-border hover:bg-v2-surface-muted'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                <Clock size={10} style={{ display: 'inline', marginRight: 4 }} /> Window
              </span>
              <select
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                className={inputClass}
              >
                {HOURS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                Agent ID
              </span>
              <input
                type="text"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                placeholder="UUID"
                className={`${inputClass} font-mono-v2`}
              />
            </label>
            <label className="block">
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                Skill ID
              </span>
              <input
                type="text"
                value={skillId}
                onChange={(e) => setSkillId(e.target.value)}
                placeholder="UUID"
                className={`${inputClass} font-mono-v2`}
              />
            </label>
            <label className="block">
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                User ID
              </span>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="UUID"
                className={`${inputClass} font-mono-v2`}
              />
            </label>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <div className="mt-4 px-3 py-2 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[12px] text-v2-danger-strong flex items-center gap-2">
          <AlertTriangle size={13} /> {error}
        </div>
      )}

      <div className="mt-4">
        {loading ? (
          <div className="text-[12px] text-v2-ink-muted py-6 text-center">Loading decisions…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Search size={20} />}
            title={totalElements === 0
              ? 'No policy decisions match these filters'
              : 'No rows on this page'}
            description={totalElements === 0
              ? 'Either no rules have evaluated yet within this window, or your filters are too narrow.'
              : 'Adjust filters or change the time window.'}
            primaryAction={
              <Button variant="primary" onClick={clearFilters}>Clear filters</Button>
            }
          />
        ) : (
          <Table
            ariaLabel="Policy decisions"
            density="compact"
            columns={[
              { key: 'decision', header: 'Decision', render: (r) => {
                const pill = DECISION_PILL[r.decision] || { tone: 'muted', label: r.decision || '—' };
                const Icon = pill.icon;
                return (
                  <Pill tone={pill.tone} size="sm" dot>
                    {Icon && <Icon size={10} style={{ display: 'inline', marginRight: 4 }} />}
                    {pill.label}
                  </Pill>
                );
              }},
              { key: 'createdAt', header: 'Evaluated', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{formatTs(r.createdAt)}</span>
              )},
              { key: 'userEmail', header: 'User', render: (r) => (
                <span className="text-[12px] text-v2-ink">
                  {r.userEmail || (r.userId ? <code className="font-mono-v2 text-[11px]">{shortUuid(r.userId)}</code> : '—')}
                </span>
              )},
              { key: 'agentId', header: 'Agent', render: (r) => (
                <code className="font-mono-v2 text-[11px] text-v2-ink-muted" title={r.agentId || ''}>
                  {shortUuid(r.agentId)}
                </code>
              )},
              { key: 'skill', header: 'Skill', render: (r) => (
                <div>
                  <div className="text-[12px] text-v2-ink truncate">{r.skillName || '—'}</div>
                  <code className="font-mono-v2 text-[10.5px] text-v2-ink-muted" title={r.skillId || ''}>
                    {shortUuid(r.skillId)}
                  </code>
                </div>
              )},
              { key: 'ruleName', header: 'Rule', render: (r) => (
                <span className="text-[12px] text-v2-ink">{r.ruleName || '—'}</span>
              )},
              { key: 'reason', header: 'Reason', render: (r) => (
                <span className="text-[11.5px] text-v2-ink-muted" title={r.violationReason || ''}>
                  {(r.violationReason || '').slice(0, 60)}{(r.violationReason || '').length > 60 ? '…' : ''}
                </span>
              )},
            ]}
            rows={rows}
            rowKey={(r) => r.id}
            onRowClick={onRowClick}
          />
        )}
      </div>

      {totalElements > 0 && (
        <div className="mt-4">
          <Pagination
            totalItems={totalElements}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="decisions"
          />
        </div>
      )}

      <PolicyDecisionDetailSlideout
        isOpen={!!selectedId}
        onClose={closeDetail}
        loading={selectedLoading}
        error={selectedError}
        detail={selectedDetail}
      />
    </div>
  );
}

export default PolicyDecisionsInventory;
